import { useEffect, useState } from 'react';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';

const PLACEHOLDER_DESCRIPTIONS = {
  '{{NOTE_TYPE}}': 'The selected template or note type name.',
  '{{PROFESSION}}': "The user's profession, when available.",
  '{{SPECIALTY}}': "The user's specialty, when available.",
  '{{AUDIENCE_CONTEXT}}':
    'A complete optional sentence built from profession and specialty.',
};

const PROMPT_BOX_HEIGHT = 420;

// Global, KV-backed prompt overrides for the Instruction Assistant. Editing here
// affects all users with the beta_prompts feature enabled.
const InstructionAssistantPrompts = ({ apiClient }) => {
  const [prompts, setPrompts] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { error: bool, text: string }

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const rsp = await apiClient.adminGetInstructionAssistantPrompts();
        const list = Array.isArray(rsp?.prompts) ? rsp.prompts : [];
        if (cancelled) return;
        setPrompts(list);
        if (list.length > 0) {
          const first = list[0];
          setSelectedKey(first.key);
          setDraft(first.overridePrompt || first.defaultPrompt || '');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading instruction assistant prompts:', err);
        setError('Unable to load Instruction Assistant prompts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const selected = prompts.find((p) => p.key === selectedKey) || null;
  const baseline = selected
    ? selected.overridePrompt || selected.defaultPrompt || ''
    : '';
  const hasOverride = Boolean(selected && selected.overridePrompt);
  const hasChanges = Boolean(selected) && draft !== baseline;

  const handleSelect = (key) => {
    const next = prompts.find((p) => p.key === key);
    setSelectedKey(key);
    setDraft(next ? next.overridePrompt || next.defaultPrompt || '' : '');
    setStatus(null);
  };

  const handleSave = async () => {
    if (!selected || !hasChanges) return;
    setSaving(true);
    setStatus(null);
    try {
      await apiClient.adminSaveInstructionAssistantPrompt(selected.key, draft);
      setPrompts((prev) =>
        prev.map((p) =>
          p.key === selected.key ? { ...p, overridePrompt: draft } : p,
        ),
      );
      setStatus({ error: false, text: 'Override saved.' });
    } catch (err) {
      console.error('Error saving instruction assistant prompt:', err);
      setStatus({ error: true, text: 'Failed to save override.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!selected || !hasOverride) return;
    setSaving(true);
    setStatus(null);
    try {
      await apiClient.adminDeleteInstructionAssistantPrompt(selected.key);
      const fallback = selected.defaultPrompt || '';
      setPrompts((prev) =>
        prev.map((p) =>
          p.key === selected.key ? { ...p, overridePrompt: '' } : p,
        ),
      );
      setDraft(fallback);
      setStatus({
        error: false,
        text: 'Override cleared. Editing default prompt.',
      });
    } catch (err) {
      console.error('Error clearing instruction assistant prompt:', err);
      setStatus({ error: true, text: 'Failed to clear override.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft(baseline);
    setStatus(null);
  };

  if (loading) {
    return <p className="text-muted">Loading prompts...</p>;
  }
  if (error) {
    return <p className="text-danger">{error}</p>;
  }
  if (prompts.length === 0) {
    return (
      <p className="text-muted">No Instruction Assistant prompts available.</p>
    );
  }

  return (
    <>
      <style>
        {`
          .ia-prompt-textarea,
          .ia-prompt-textarea:focus {
            background-color: #ffffff !important;
            color: #525f7f;
            transition: none !important;
          }
          .ia-prompt-textarea {
            border-color: #d8dee8;
            border-radius: 6px;
            box-shadow: none;
            height: ${PROMPT_BOX_HEIGHT}px;
            line-height: 1.5;
            min-height: 240px;
            padding: 12px 14px;
          }
          .ia-prompt-textarea:focus {
            border-color: #5e72e4;
            box-shadow: 0 0 0 1px rgba(94, 114, 228, 0.2);
          }
          .ia-prompt-default-panel {
            background: #f8fafc;
            border: 1px solid #d8dee8;
            border-radius: 6px;
            color: #525f7f;
            font-family: monospace;
            font-size: 0.8rem;
            height: ${PROMPT_BOX_HEIGHT}px;
            line-height: 1.5;
            overflow-y: auto;
            padding: 12px 14px;
            white-space: pre-wrap;
          }
          .ia-placeholder-chip {
            background: #f8fafc;
            border: 1px solid #d8dee8;
            border-radius: 999px;
            color: #344767;
            cursor: help;
            font-family: monospace;
            font-size: 0.78rem;
            font-weight: 600;
            padding: 3px 9px;
          }
          .ia-secondary-action,
          .ia-secondary-action:focus {
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: none !important;
            color: #344767 !important;
          }
          .ia-secondary-action:hover:not(:disabled) {
            background: #f1f5f9 !important;
            border-color: #aeb8c7 !important;
            color: #26364f !important;
          }
          .ia-secondary-action:disabled {
            background: #f8fafc !important;
            border-color: #d8dee8 !important;
            color: #8392ab !important;
            opacity: 1 !important;
          }
        `}
      </style>

      <div
        style={{
          alignItems: 'flex-start',
          borderBottom: '1px solid #edf0f5',
          display: 'flex',
          gap: '16px',
          justifyContent: 'space-between',
          marginBottom: '18px',
          paddingBottom: '16px',
        }}
      >
        <div>
          <h5 style={{ color: '#32325d', fontWeight: 700, marginBottom: '4px' }}>
            Instruction Assistant Prompts
          </h5>
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
            Global overrides for users with <code>beta_prompts</code> enabled.
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '18px' }}>
        <Row>
          <Col md={4}>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label for="iaPromptSelect" style={{ color: '#32325d', fontWeight: 600 }}>
                Prompt
              </Label>
              <Input
                id="iaPromptSelect"
                type="select"
                value={selectedKey}
                onChange={(e) => handleSelect(e.target.value)}
                style={{ borderColor: '#d8dee8', borderRadius: '6px' }}
              >
                {prompts.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label || p.key}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={8}>
            {selected?.placeholders?.length > 0 && (
              <div>
                <div
                  style={{
                    color: '#32325d',
                    fontWeight: 600,
                    marginBottom: '8px',
                  }}
                >
                  Available placeholders
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                  {selected.placeholders.map((ph) => (
                    <span
                      className="ia-placeholder-chip"
                      key={ph}
                      title={PLACEHOLDER_DESCRIPTIONS[ph] || ph}
                    >
                      {ph}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Col>
        </Row>
      </div>

      <Row>
        <Col md={6}>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ color: '#32325d', fontWeight: 700 }}>
              Override Prompt
              <span
                className="text-muted"
                style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: '6px' }}
              >
                saved globally
              </span>
            </div>
          </div>
          <FormGroup>
            <Input
              type="textarea"
              className="ia-prompt-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={16}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                resize: 'vertical',
              }}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ color: '#32325d', fontWeight: 700 }}>
              Default Prompt
              <span
                className="text-muted"
                style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: '6px' }}
              >
                source code
              </span>
            </div>
          </div>
          <FormGroup>
            <div className="ia-prompt-default-panel">
              {selected?.defaultPrompt || ''}
            </div>
          </FormGroup>
        </Col>
      </Row>

      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          paddingTop: '4px',
        }}
      >
        <span
          style={{ display: 'inline-block' }}
          title="Save this text as the active global override for the selected prompt."
        >
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={{ boxShadow: 'none' }}
          >
            {saving ? 'Saving...' : 'Save Override'}
          </Button>
        </span>
        <span
          style={{ display: 'inline-block' }}
          title="Discard unsaved edits and return to the last loaded prompt text."
        >
          <Button
            className="ia-secondary-action"
            onClick={handleReset}
            disabled={!hasChanges || saving}
          >
            Reset
          </Button>
        </span>
        <span
          style={{ display: 'inline-block' }}
          title="Delete the saved override so beta users fall back to the default prompt."
        >
          <Button
            color="danger"
            outline
            onClick={handleClear}
            disabled={!hasOverride || saving}
          >
            Clear Override
          </Button>
        </span>
        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
          {hasOverride
            ? 'Active override in effect'
            : 'No override saved'}
        </span>
        {status && (
          <span
            className={status.error ? 'text-danger' : 'text-success'}
            style={{
              fontSize: '0.9rem',
              fontWeight: 600,
              marginLeft: 'auto',
            }}
          >
            {status.text}
          </span>
        )}
      </div>
    </>
  );
};

export default InstructionAssistantPrompts;
